import { ANALYTICS_REPORT_PAGES, analyticsReportPage, analyticsReportView } from "./analytics-pages";
import {
  isAccountingRole,
  isAdminRole,
  isAdminTierRole,
  isOrgAdmin,
  isOrgBillingManager,
  isOutreachRole,
  isSuperAdminRole,
  type GlobalRole,
} from "./roles";
import {
  OUTREACH_RECORD_ROUTES,
  RECORD_ROUTE_LIST_PAGES,
  adminPageForHref,
  type AdminCapability,
  type AdminPage,
} from "./admin-pages";

export const PAGE_ACCESS_STATES = ["allow", "deny"] as const;
export type PageAccessState = (typeof PAGE_ACCESS_STATES)[number];

export interface AdminAccessOverrides {
  pages?: Record<string, PageAccessState>;
}

export interface AccessSubject {
  role?: GlobalRole | string;
  adminAccess?: AdminAccessOverrides | null;
}

export function roleHasCapability(
  role: string | undefined,
  capability: AdminCapability,
): boolean {
  if (capability === "finance") return isAccountingRole(role);
  if (capability === "management") return isAdminTierRole(role);
  return isSuperAdminRole(role);
}

export function pageAllowedByRole(page: AdminPage, role: string | undefined): boolean {
  if (isOutreachRole(role) && !isAdminRole(role)) return page.outreach === true;
  if (!isAdminRole(role)) return false;
  return page.capability ? roleHasCapability(role, page.capability) : true;
}

export function overrideFor(
  subject: AccessSubject,
  href: string,
): PageAccessState | undefined {
  const pages = subject.adminAccess?.pages;
  const current = "/admin/billing/order-financials";
  const legacy = "/admin/analytics/order-financials";
  if (href === current || href === legacy) return pages?.[current] ?? pages?.[legacy];
  return pages?.[href];
}

export function canAccessPage(subject: AccessSubject, href: string): boolean {
  const page = adminPageForHref(href);
  if (!page) return isAdminRole(subject.role);
  if (isSuperAdminRole(subject.role)) return true;
  if (!isAdminRole(subject.role)) return pageAllowedByRole(page, subject.role);
  if (overrideFor(subject, page.href) === "deny") return false;
  return pageAllowedByRole(page, subject.role);
}

export function adminListPageForHref(href: string): AdminPage | undefined {
  const segments = (href.split(/[?#]/)[0] ?? href).split("/").filter(Boolean);
  const exact = adminPageForHref(`/${segments.join("/")}`);
  if (exact) return exact;
  for (let depth = segments.length; depth >= 2; depth--) {
    const candidate = `/${segments.slice(0, depth).join("/")}`;
    const page = adminPageForHref(RECORD_ROUTE_LIST_PAGES[candidate] ?? candidate);
    if (page) return page;
  }
  return undefined;
}

function isOutreachRecordHref(href: string): boolean {
  const segments = (href.split(/[?#]/)[0] ?? href).split("/").filter(Boolean);
  const route = `/${segments.slice(0, 2).join("/")}`;
  return segments.length === 3 && OUTREACH_RECORD_ROUTES.includes(route);
}

export function canAccessAdminHref(subject: AccessSubject, href: string): boolean {
  const reportPage = analyticsReportPage(href);
  if (reportPage === "/admin/analytics/demand" && analyticsReportView(href) === "acquisition") return canAccessPage(subject, reportPage) || canAccessPage(subject, "/admin/ads");
  if (reportPage) return canAccessPage(subject, reportPage);
  if (href.split(/[?#]/)[0] === "/admin/analytics") return ANALYTICS_REPORT_PAGES.some((page) => canAccessPage(subject, page));
  if (!isAdminRole(subject.role) && isOutreachRole(subject.role) && isOutreachRecordHref(href)) {
    return true;
  }
  const page = adminListPageForHref(href);
  return page ? canAccessPage(subject, page.href) : isAdminRole(subject.role);
}

export function canOpenAdminHref(
  subject: AccessSubject | undefined,
  href: string,
): boolean {
  return subject !== undefined && canAccessAdminHref(subject, href);
}

export function canSeeNavLink(subject: AccessSubject, href: string): boolean {
  if (href.split("?")[0] === "/admin/analytics") return canAccessAdminHref(subject, href);
  if (!canAccessAdminHref(subject, href)) return false;
  const navCapability = adminListPageForHref(href)?.navCapability;
  return navCapability ? roleHasCapability(subject.role, navCapability) : true;
}

export function hasCapability(
  subject: AccessSubject,
  capability: AdminCapability,
): boolean {
  return roleHasCapability(subject.role, capability);
}

export function canManageCompanyBillingFor(
  subject: AccessSubject,
  membershipRole: string | undefined,
): boolean {
  return hasCapability(subject, "finance") || isOrgBillingManager(membershipRole);
}

export function canAlignPayoutEntityFor(
  subject: AccessSubject,
  membershipRole: string | undefined,
): boolean {
  return hasCapability(subject, "finance") || isOrgAdmin(membershipRole);
}

export type PageAccessSource = "super_admin" | "override" | "role" | "outreach";

export interface PageAccessVerdict {
  href: string;
  allowed: boolean;
  source: PageAccessSource;
  override?: PageAccessState;
}

export function explainPageAccess(subject: AccessSubject, page: AdminPage): PageAccessVerdict {
  if (isSuperAdminRole(subject.role)) {
    return { href: page.href, allowed: true, source: "super_admin" };
  }
  const override = isAdminRole(subject.role) ? overrideFor(subject, page.href) : undefined;
  if (override === "deny") {
    return { href: page.href, allowed: false, source: "override", override };
  }
  return {
    href: page.href,
    allowed: pageAllowedByRole(page, subject.role),
    source: isAdminRole(subject.role) ? "role" : "outreach",
    override,
  };
}
